/**
 * Which edition a tenant belongs to.
 *
 * Keyed off the endpoint rather than the host, because a Maikoz tenant is
 * reachable from either cockpit and the operator should meet the same product
 * both ways. AdminServerService keeps the list (`BrandByEndpoint` in
 * appsettings) and sends the answer as `EndpointBrand`.
 *
 * Returns null when the endpoint has no brand of its own — the caller then
 * falls back to the per-host theme pack, which is what brands a cockpit before
 * an endpoint is even picked.
 */

/**
 * THE list of brands — the only place one is declared. `ThemePack` in
 * themeProvider.tsx is this same type, and every per-brand table (logos, pack
 * labels, assistant profiles) is a `Record<BrandId, …>`, so adding a brand here
 * makes the compiler name every table still missing it. The pack's colours live
 * in `styles/tokens/theme-<id>.css`, which no type can check.
 */
export const BRAND_IDS = ["instantsystems", "maikoz", "ctlp"] as const;

export type BrandId = (typeof BRAND_IDS)[number];

export function isBrand(value: unknown): value is BrandId {
  return (BRAND_IDS as readonly unknown[]).includes(value);
}

/**
 * Endpoint IDs pinned to a brand. Only for a server that predates
 * `EndpointBrand`: the server's `BrandByEndpoint` is the list, and this copy
 * goes once every server sends it.
 */
const BRAND_BY_ENDPOINT_ID: Record<number, BrandId> = {
  138: "maikoz",
  145: "maikoz",
};

/** Product name per brand, for the surfaces that would otherwise take the
 *  host's. Absent falls back to the per-host `WindowTitle`. */
const BRAND_TITLES: Partial<Record<BrandId, string>> = {
  instantsystems: "Instant Cockpit",
  maikoz: "Maikoz Cockpit",
};

export function resolveEndpointBrand(endpointId: number | null | undefined): BrandId | null {
  const pinned = endpointId ? BRAND_BY_ENDPOINT_ID[endpointId] : undefined;
  return isBrand(pinned) ? pinned : null;
}

/**
 * The endpoint's own brand: the server's answer whenever it gives one ("" means none), and
 * BRAND_BY_ENDPOINT_ID only for a server that predates `EndpointBrand`.
 */
export function resolveEndpointPin(serverBrand: unknown, endpointId: number | null | undefined): BrandId | null {
  if (typeof serverBrand === "string") return isBrand(serverBrand) ? serverBrand : null;
  return resolveEndpointBrand(endpointId);
}

export function brandTitle(brand: BrandId | null): string | null {
  return (brand && BRAND_TITLES[brand]) ?? null;
}

export type BrandSource = "picked" | "endpoint" | "host" | "default";

export interface ResolvedBrand {
  id: BrandId;
  source: BrandSource;
  /** The brand's own product name when a pick or the endpoint chose it, else the host's `WindowTitle`. */
  title: string;
}

/**
 * The one order every brand surface follows, the same as the theme pack that
 * paints the chrome: a SuperAdmin pick, then the endpoint, then the host.
 */
export function resolveBrand(inputs: {
  picked?: unknown;
  endpointId?: number | null;
  serverBrand?: unknown;
  hostPack?: unknown;
  hostTitle: string;
}): ResolvedBrand {
  const endpoint = resolveEndpointPin(inputs.serverBrand, inputs.endpointId);
  const [id, source]: [BrandId, BrandSource] =
    isBrand(inputs.picked) ? [inputs.picked, "picked"]
      : endpoint ? [endpoint, "endpoint"]
        : isBrand(inputs.hostPack) ? [inputs.hostPack, "host"]
          : ["instantsystems", "default"];
  const own = source === "picked" || source === "endpoint" ? brandTitle(id) : null;
  return { id, source, title: own ?? inputs.hostTitle };
}

/**
 * The endpoint only arrives after sign-in resolves, so without a hint a Maikoz
 * operator watches the cockpit repaint from red to teal on every load. The last
 * resolved brand is remembered purely to paint the first frame; the real answer
 * overwrites it either way, so a stale value costs one repaint — exactly what it
 * costs today without it.
 */
const LAST_BRAND_KEY = "webadmincc-brand-last";

export function readLastBrand(): BrandId | null {
  try {
    const stored = localStorage.getItem(LAST_BRAND_KEY);
    return isBrand(stored) ? stored : null;
  } catch {
    return null;
  }
}

export function rememberBrand(brand: BrandId | null): void {
  try {
    if (brand) localStorage.setItem(LAST_BRAND_KEY, brand);
    else localStorage.removeItem(LAST_BRAND_KEY);
  } catch { /* private mode — the hint is optional */ }
}
